import storage from "@/lib/storage";
import axios from "axios";
import { toast } from "sonner";

const getBaseURL = (): string => {
  // Use NEXT_PUBLIC_BASE_URL if set, otherwise fall back to relative path
  if (process.env.NEXT_PUBLIC_BASE_URL) {
    return `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1`;
  }

  // Fallback to relative path (works when UI and API are on same domain)
  return "/api/v1";
};

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000,
  // headers: {
  //   "Content-Type": "application/json",
  // },
});

// ── Activity Log auto-logging ──

// Endpoints to skip logging (avoid infinite loop + noisy background calls)
const SKIP_LOG_PATTERNS = [
  '/user/activity/',
  '/user-state',
  '/robot/mower/get-params',
  '/robot/mission/cloud-sync',
];

const shouldLogRequest = (config: any): boolean => {
  const method = config?.method?.toLowerCase();
  // Only log mutating requests (POST, PUT, PATCH, DELETE)
  if (!method || method === 'get') return false;
  const url = config?.url || '';
  return !SKIP_LOG_PATTERNS.some(p => url.includes(p));
};

// Build a human-readable title from the URL path
// e.g. "/admin/orchard/create" → "Orchard create"
// e.g. "/admin/robot/mission/start" → "Mission start"
const buildTitle = (url: string, method: string): string => {
  const clean = url.split('?')[0].replace(/^\//, '');
  const parts = clean.split('/').filter(Boolean);
  // Drop prefix segments like "admin", "user", "robot"
  const meaningful = parts.filter(p => !['admin', 'user', 'robot', 'v1'].includes(p));
  if (meaningful.length === 0) return `${method.toUpperCase()} ${url}`;
  // Capitalize first segment, join rest
  const label = meaningful.map(s => s.replace(/-/g, ' ')).join(' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// Extract robotId from request body or URL query
const extractRobotId = (config: any): string => {
  // Check request body
  const data = config?.data;
  if (data) {
    try {
      const body = typeof data === 'string' ? JSON.parse(data) : data;
      if (body?.robotId) return body.robotId;
    } catch {}
  }
  // Check URL params
  const url = config?.url || '';
  const match = url.match(/robotId=([^&]+)/);
  if (match) return match[1];
  // Try to extract from the current page URL
  if (typeof window !== 'undefined') {
    const pathMatch = window.location.pathname.match(/\/(?:create-geofence|mission-control|dashboard|teleop-new)\/([^/]+)/);
    if (pathMatch) return pathMatch[1];
  }
  return '';
};

// Get current screen from page URL
const getCurrentScreen = (): string => {
  if (typeof window === 'undefined') return 'unknown';
  const path = window.location.pathname;
  if (path.includes('/create-geofence') || path.includes('/mission-control')) return 'geofence';
  if (path.includes('/dashboard')) return 'dashboard';
  if (path.includes('/teleop')) return 'teleop';
  if (path.includes('/fleet-overview')) return 'fleet-overview';
  return path.replace(/^\//, '') || 'unknown';
};

// Fire-and-forget activity log — never blocks the original request
const logActivity = (
  config: any,
  statusCode: number,
  result: 'success' | 'warning' | 'failure',
  message: string
) => {
  try {
    const robotId = extractRobotId(config);
    if (!robotId) return; // Can't log without a robotId

    const url = config?.url || '';
    const method = config?.method?.toUpperCase() || '';
    const title = buildTitle(url, method);

    api.post('/user/activity/add', {
      timestamp: new Date().toISOString(),
      title,
      message,
      event: `${method}:${url.split('?')[0]}`,
      code: statusCode,
      result,
      screen: getCurrentScreen(),
      robotId,
    }).catch(() => {}); // Silently ignore failures
  } catch {}
};

// Request interceptor
api.interceptors.request.use(
  config => {
    // Only access localStorage on the client side
    if (typeof window !== "undefined") {
      const token = storage.getItem("token");

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  error => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Check if request is a silent background operation (no toasts)
const isSilentRequest = (config: any) =>
  config?.url?.includes('/user-state') ||
  config?.url?.includes('/robot/mower/') ||
  config?.url?.includes('/user/activity/');

// Response interceptor
api.interceptors.response.use(
  response => {
    const data = response?.data;
    const statusCode = response?.data?.statusCode;
    const message = response?.data?.message;

    if ((statusCode === 200 || statusCode === 201) && response?.config?.method !== "get") {
      if (!isSilentRequest(response?.config)) {
        toast.success(message || "Operation successful");
      }
    }

    // Auto-log successful mutating requests
    if (shouldLogRequest(response?.config)) {
      logActivity(response?.config, statusCode || 200, 'success', message || 'Operation successful');
    }

    return data;
  },
  error => {
    const data = error?.response?.data;

    // Auto-log failed mutating requests. Prefer the FM error code from the
    // response body (e.g. 1001 — see docs/error-codes.md) over the HTTP
    // status code (500/400) so the activity log shows the same number that
    // the FM Error Code Reference documents.
    if (shouldLogRequest(error?.config)) {
      const httpStatus = error?.response?.status || 0;
      const fmCode = error?.response?.data?.statusCode;
      const code = typeof fmCode === 'number' ? fmCode : httpStatus;
      const msg = error?.response?.data?.message || error?.message || 'Request failed';
      logActivity(error?.config, code, httpStatus >= 500 ? 'failure' : 'warning', msg);
    }

    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.message;
      const silent = isSilentRequest(error.config);

      switch (status) {
        case 400:
          if (!silent) toast.error(message || "Bad request");
          break;
        case 401:
          // Only redirect to login if we're not already on the login page
          if (typeof window !== "undefined" && !window.location.pathname.includes("/login")) {
            // Check if the token actually exists - if it does, it's expired or invalid
            const token = storage.getItem("token");
            if (token) {
              toast.error(message || "Session expired. Please login again.");
              storage.removeItem("token");
              window.location.href = "/login";
            }
          }
          break;
        case 403:
          // Access denied - let the caller handle this error
          break;
        case 404:
          if (!silent) toast.error(message || "Resource not found");
          break;
        case 422:
          if (!silent) toast.error(message || "Validation error");
          break;
        case 429:
          if (!silent) toast.error(message || "Too many requests. Please try again later.");
          break;
        case 500:
          if (!silent) toast.error(message || "Server error. Please try again.");
          break;
        default:
          if (!silent) toast.error(message || "Something went wrong");
      }
    } else if (error.request) {
      if (!isSilentRequest(error.config)) toast.error("Network error. Please check your connection.");
    } else {
      toast.error("An unexpected error occurred");
    }

    // Return a properly structured error object instead of just data
    return Promise.reject({
      message: error.response?.data?.message || error.message || "An error occurred",
      status: error.response?.status,
      data: data,
      originalError: error,
    });
  }
);

export { api };
export default api;
